import { Component, OnInit, Input, SimpleChanges, OnChanges, EventEmitter, Output } from '@angular/core';
import { classToClass, plainToClass } from 'class-transformer';

import {GrowlService, UserService} from '@app/core';
import { DialogStyle65, DTColumn } from '@app/shared/classes/common';
import { ApprovalStatusType } from '@app/shared/enums/approval-status-type.enum';
import { ClassType } from '@app/shared/enums/class-type.enum';
import { Format } from '@app/shared/enums/format.enum';
import { GrowlType } from '@app/shared/enums/growl-type.enum';

import { ProjectTask, ProjectTaskBalance, ProjectResourceBalance, ProjectResource } from '@app/shared/classes/project';
import { SolutionManagerDataService } from '@app/solution-manager/solution-manager-data.service';
import {PrimeNgConfig, Roles } from '..';

@Component({
  selector: 'app-project-resource-balance',
  templateUrl: './project-resource-balance.component.html',
  styleUrls: ['./project-resource-balance.component.css'],
  providers: [SolutionManagerDataService]
})
export class ProjectResourceBalanceComponent implements OnInit, OnChanges {

  @Input() selectedProjectTask: ProjectTask;
  @Input() displayResourceBalanceDialog: boolean;
  @Input() inSolutionManager: boolean;
  @Input() goLiveAccountingPeriodClosed: boolean;
  @Output() handler: EventEmitter<any> = new EventEmitter();

  DialogStyle65 = DialogStyle65;
  ApprovalStatusType: typeof ApprovalStatusType = ApprovalStatusType;
  EmployeeType: typeof EmployeeType = EmployeeType;
  Format: typeof Format = Format;
  ClassType: typeof ClassType = ClassType;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;
  Roles: typeof Roles = Roles;

  selectedProjectResourceBalances: ProjectResourceBalance[] = [];
  resourceBalanceColumns: DTColumn[] = [];

  selectedProjectTaskBalance: ProjectTaskBalance = new ProjectTaskBalance();

  constructor (private dataService: SolutionManagerDataService, private growl: GrowlService, private user: UserService) { }

  ngOnInit() {
    this.setupProjectResourceBalanceColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.selectedProjectTask.id) {
      this.dataService.getProjectTaskBalances(this.selectedProjectTask.id, this.inSolutionManager).subscribe(res => {
        console.log(res['list']);
        this.selectedProjectTaskBalance = res['list'][0];
        this.initializeProjectResourceBalanceTable(this.selectedProjectTask, res['list']);
      });
    }
  }

  setupProjectResourceBalanceColumns() {
    this.resourceBalanceColumns.push({ field: 'projectResourceName', header: 'Project Resource' });
    this.resourceBalanceColumns.push({ field: 'projectResourceLaborCategoryPositionTitle', header: 'Labor Category' });
    this.resourceBalanceColumns.push({ field: 'actualHours', header: 'Actual Hours ITD' });
    this.resourceBalanceColumns.push({ field: 'employeeLaborYTD', header: 'Employee Labor YTD' });
    this.resourceBalanceColumns.push({ field: 'employeeLaborITD', header: 'Employee Labor ITD' });
    this.resourceBalanceColumns.push({ field: 'subcontractorLaborYTD', header: 'Subcontractor Labor YTD' });
    this.resourceBalanceColumns.push({ field: 'subcontractorLaborITD', header: 'Subcontractor Labor ITD' });
  }


  initializeProjectResourceBalanceTable(projectTask: ProjectTask, projectTaskBalances: ProjectTaskBalance[]) {
    let projectResources: ProjectResource[] = classToClass(projectTask.resources);
    let existingProjectResourceBalances: ProjectResourceBalance[] = projectTaskBalances.length == 1 ? projectTaskBalances[0].projectResourceBalances : [];

    for (let projectResource of projectResources) {
      if (projectResource.userId != null) {
        let projectResourceBalanceIndex: number = existingProjectResourceBalances.findIndex(projectResourceBalanceOb => projectResourceBalanceOb.projectResourceId == projectResource.id);
        if (projectResourceBalanceIndex > -1) {
          this.createProjectResourceBalanceEntry(projectResource, existingProjectResourceBalances[projectResourceBalanceIndex]);
        } else {
          this.createProjectResourceBalanceEntry(projectResource);
        }
      }
    }


  }

  createProjectResourceBalanceEntry(projectResource: ProjectResource, existingProjectResourceBalance?: ProjectResourceBalance) {
    let projectResourceBalance: ProjectResourceBalance = new ProjectResourceBalance();
    projectResourceBalance.projectResourceId = projectResource.id;
    projectResourceBalance.projectResourceName = projectResource.userFullName;
    projectResourceBalance.projectResourceLaborCategoryPositionTitle = projectResource.positionTitle;
    if (existingProjectResourceBalance) {
      projectResourceBalance.actualHours = existingProjectResourceBalance.actualHours;
      projectResourceBalance.employeeLaborITD = existingProjectResourceBalance.employeeLaborITD;
      projectResourceBalance.subcontractorLaborITD = existingProjectResourceBalance.subcontractorLaborITD;
      projectResourceBalance.employeeLaborYTD = existingProjectResourceBalance.employeeLaborYTD;
      projectResourceBalance.subcontractorLaborYTD = existingProjectResourceBalance.subcontractorLaborYTD;
    }
    this.selectedProjectResourceBalances.push(projectResourceBalance);
  }


  calculateTotalITD(totalType) {
    if (totalType == EmployeeType.Employee) {
      let employeeLaborTotal = this.selectedProjectResourceBalances.reduce((a, b) => a + b.employeeLaborITD, 0);
      return employeeLaborTotal;
    } else if (totalType == EmployeeType.SubContractor) {
      let subcontractorLaborTotal = this.selectedProjectResourceBalances.reduce((a, b) => a + b.subcontractorLaborITD, 0);
      return subcontractorLaborTotal;
    }
  }

  calculateTotalYTD(totalType) {
    if (totalType == EmployeeType.Employee) {
      let employeeLaborTotal = this.selectedProjectResourceBalances.reduce((a, b) => a + b.employeeLaborYTD, 0);
      return employeeLaborTotal;
    } else if (totalType == EmployeeType.SubContractor) {
      let subcontractorLaborTotal = this.selectedProjectResourceBalances.reduce((a, b) => a + b.subcontractorLaborYTD, 0);
      return subcontractorLaborTotal;
    }
  }

  calculateTotalHours() {
    return this.selectedProjectResourceBalances.reduce((a, b) => a + b.actualHours, 0);
  }


  cancelProjectResourceBalance() {
    this.hideDialog();
  }

  rollUpResourceBalances() {
    this.dataService.rollUpProjectTaskBalance(this.selectedProjectTaskBalance).subscribe(res => {
      this.selectedProjectTaskBalance = plainToClass(ProjectTaskBalance, res);
      this.growl.add(GrowlType.Success, "Rollup balances have been updated");
    });
  }

  saveProjectResourceBalance() {
    this.selectedProjectTaskBalance.projectResourceBalances = classToClass(this.selectedProjectResourceBalances, {
      excludePrefixes: ['projectResourceName', 'projectResourceLaborCategoryPositionTitle']
    });
    this.dataService.updateProjectTaskBalance(this.selectedProjectTaskBalance, this.inSolutionManager).subscribe(res => {
      this.selectedProjectTaskBalance = plainToClass(ProjectTaskBalance, res);
      this.growl.add(GrowlType.Success, "Task Balance saved successfully");
      this.hideDialog();
    });
  }

  hideDialog(event?) {
    this.handler.emit(this.selectedProjectTaskBalance);
  }

}

enum EmployeeType {
  Employee = "EMPLOYEE",
  SubContractor = "SUB_CONTRACTOR"
}
